import {
  cellArea,
  cellToChildren,
  cellToLatLng,
  cellToParent,
  cellsToMultiPolygon,
  getResolution,
  gridDisk,
  latLngToCell,
  UNITS,
} from 'h3-js';

/** Resolução usada para preenchimento amplo por raio (≈ 5,16 km² por célula, aresta ≈ 1,2 km). */
export const H3_RESOLUTION = 7;

/**
 * Resolução usada pelo pincel/borracha manual (ajuste fino), aresta ≈ 174 m,
 * área ≈ 0,105 km² por célula (≈ 1/49 da resolução de preenchimento).
 * O preenchimento por raio continua em H3_RESOLUTION para não explodir o
 * número de células em áreas grandes (até 50 km de raio).
 */
export const H3_BRUSH_RESOLUTION = 9;

/** Aresta média aproximada de uma célula na resolução padrão, em metros. */
const CELL_EDGE_METERS = 1220;

/** Distância aproximada (metros) entre dois pontos, fórmula de Haversine. */
function haversineMeters(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Todas as células cujo centro está dentro do raio informado a partir de um ponto
 * (mais uma margem de meia aresta — ver abaixo).
 * Usada para preencher automaticamente uma área a partir de centro + raio,
 * mantendo o resultado como área desenhada (editável com pincel/borracha).
 */
export function cellsWithinRadius(
  center: [number, number],
  radiusMeters: number,
  resolution = H3_RESOLUTION,
): string[] {
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return [];
  const originCell = latLngToCell(center[0], center[1], resolution);
  const rings = Math.max(1, Math.min(60, Math.ceil(radiusMeters / CELL_EDGE_METERS) + 1));
  // Margem de meia aresta: sem ela, um hexágono cujo CENTRO cai um pouco além
  // do raio era descartado inteiro, mesmo cobrindo boa parte do círculo
  // visualmente (aresta ≈ 1,2 km, ou seja, o hexágono pode se estender bem
  // além do próprio centro). Fornecedores relatavam áreas desenhadas que
  // "pareciam" cobrir um CEP no mapa mas eram rejeitadas na validação real.
  const effectiveRadius = radiusMeters + CELL_EDGE_METERS / 2;
  try {
    return gridDisk(originCell, rings).filter((cell) => {
      const [lat, lng] = cellToLatLng(cell);
      return haversineMeters(center, [lat, lng]) <= effectiveRadius;
    });
  } catch (error) {
    console.error('[h3Cells] Falha ao gerar células a partir do raio:', error);
    return [originCell];
  }
}


/**
 * Distância (em linha reta, metros) da origem até a célula mais próxima e a mais distante.
 *
 * Fonte única de medição das áreas desenhadas: usa o CENTRO das células (e não os
 * vértices dos hexágonos), para que o resultado bata com o raio usado no preenchimento.
 */
export function cellsDistanceRangeMeters(
  cells: string[] | null | undefined,
  origin: [number, number] | null | undefined,
): { minMeters: number; maxMeters: number } | null {
  if (!cells || cells.length === 0 || !origin) return null;
  let minMeters = Infinity;
  let maxMeters = 0;
  for (const cell of cells) {
    let center: [number, number];
    try {
      const [lat, lng] = cellToLatLng(cell);
      center = [lat, lng];
    } catch {
      continue;
    }
    const distance = haversineMeters(origin, center);
    if (distance < minMeters) minMeters = distance;
    if (distance > maxMeters) maxMeters = distance;
  }
  if (!Number.isFinite(minMeters)) return null;
  return { minMeters, maxMeters };
}

export type LatLng = [number, number];

/** Converte um ponto do mapa na célula H3 correspondente. */
export function pointToCell(lat: number, lng: number, resolution = H3_RESOLUTION): string {
  return latLngToCell(lat, lng, resolution);
}

/**
 * Retorna a célula do ponto mais os vizinhos até `rings` anéis.
 * rings = 0 → 1 célula, 1 → 7 células, 2 → 19 células, 3 → 37 células.
 */
export function brushCells(lat: number, lng: number, rings: number, resolution = H3_RESOLUTION): string[] {
  const center = latLngToCell(lat, lng, resolution);
  const safeRings = Math.max(0, Math.min(6, Math.round(rings)));
  if (safeRings === 0) return [center];
  return gridDisk(center, safeRings);
}

/**
 * Adiciona uma célula fina a um conjunto, sem duplicar área: se algum ancestral
 * mais grosso já cobre esse ponto (ex.: região preenchida por raio em
 * H3_RESOLUTION), a célula fina é ignorada por já estar coberta.
 * Retorna true se o conjunto mudou.
 */
export function addFineCell(cells: Set<string>, cell: string, resolution = H3_BRUSH_RESOLUTION): boolean {
  if (cells.has(cell)) return false;
  for (let r = resolution - 1; r >= 0; r--) {
    try {
      if (cells.has(cellToParent(cell, r))) return false;
    } catch {
      break;
    }
  }
  cells.add(cell);
  return true;
}

/**
 * Remove uma célula fina de um conjunto. Se o ponto está coberto por um
 * ancestral mais grosso (ex.: área antiga desenhada em H3_RESOLUTION), o
 * ancestral é "quebrado" em seus filhos na resolução fina, mantendo todos
 * menos o que está sendo apagado — evita buracos ao apagar regiões antigas.
 * Retorna true se o conjunto mudou.
 */
export function removeFineCell(cells: Set<string>, cell: string, resolution = H3_BRUSH_RESOLUTION): boolean {
  if (cells.delete(cell)) return true;
  for (let r = resolution - 1; r >= 0; r--) {
    let ancestor: string;
    try {
      ancestor = cellToParent(cell, r);
    } catch {
      break;
    }
    if (!cells.has(ancestor)) continue;
    cells.delete(ancestor);
    for (const child of cellToChildren(ancestor, resolution)) {
      if (child !== cell) cells.add(child);
    }
    return true;
  }
  return false;
}

/**
 * Agrupa e converte um conjunto de células em multipolígonos dissolvidos
 * ([lat, lng]), um grupo por resolução — pronto para várias layers do
 * Leaflet (uma por grupo).
 *
 * O h3-js recusa dissolver células de resoluções diferentes na mesma chamada
 * ("Cell arguments had incompatible resolutions"). Como uma área pode
 * combinar células antigas (H3_RESOLUTION) com ajustes finos do pincel
 * (H3_BRUSH_RESOLUTION), agrupamos por resolução e dissolvemos cada grupo
 * separadamente.
 *
 * Importante: cada grupo precisa virar uma forma (`L.polygon`) PRÓPRIA, não
 * uma única forma combinada. Quando o apagar "quebra" uma célula antiga
 * grande em várias finas, o grupo grosso passa a ter um buraco exatamente
 * onde a célula fina entra — se as duas formas fossem desenhadas como um
 * único caminho SVG, o preenchimento da célula fina pode "cancelar" o
 * buraco do grupo grosso pela regra de preenchimento (nonzero/evenodd),
 * criando um buraco fantasma na tela mesmo com os dados corretos.
 */
export function cellsToLeafletMultiPolygonGroups(cells: string[]): LatLng[][][][] {
  if (!cells || cells.length === 0) return [];

  const byResolution = new Map<number, string[]>();
  for (const cell of cells) {
    let res: number;
    try {
      res = getResolution(cell);
    } catch {
      continue;
    }
    const group = byResolution.get(res);
    if (group) group.push(cell);
    else byResolution.set(res, [cell]);
  }

  const groups: LatLng[][][][] = [];
  for (const group of byResolution.values()) {
    try {
      // GeoJSON = false → coordenadas em [lat, lng], como o Leaflet espera
      groups.push(cellsToMultiPolygon(group, false) as LatLng[][][]);
    } catch (error) {
      console.error('[h3Cells] Falha ao converter células em polígonos:', error);
    }
  }
  return groups;
}

/** Área total aproximada, em km², de um conjunto de células. */
export function cellsAreaKm2(cells: string[]): number {
  if (!cells || cells.length === 0) return 0;
  try {
    return cells.reduce((sum, cell) => sum + cellArea(cell, UNITS.km2), 0);
  } catch {
    return 0;
  }
}

/** Centro geográfico aproximado de um conjunto de células (média dos vértices do contorno). */
export function cellsCentroid(cells: string[]): LatLng | null {
  const groups = cellsToLeafletMultiPolygonGroups(cells);
  let latSum = 0;
  let lngSum = 0;
  let count = 0;
  for (const polygons of groups) {
    for (const polygon of polygons) {
      for (const ring of polygon) {
        for (const [lat, lng] of ring) {
          latSum += lat;
          lngSum += lng;
          count += 1;
        }
      }
    }
  }
  if (count === 0) return null;
  return [latSum / count, lngSum / count];
}

/** True quando a área possui células pintadas. */
export function hasCells(cells?: string[] | null): boolean {
  return Array.isArray(cells) && cells.length > 0;
}

const cellSetCache = new WeakMap<object, { set: Set<string>; resolutions: number[] }>();

/**
 * True quando o ponto está dentro da área desenhada. A área pode misturar células de
 * resoluções diferentes (preenchimento por raio em res. 7 + pincel fino em res. 9),
 * então o ponto é convertido para cada resolução presente na área.
 */
export function areaCoversPoint(cells: string[] | null | undefined, lat: number, lng: number): boolean {
  if (!Array.isArray(cells) || cells.length === 0) return false;
  let entry = cellSetCache.get(cells);
  if (!entry) {
    const resolutions = new Set<number>();
    for (const c of cells) {
      try { resolutions.add(getResolution(c)); } catch { /* célula inválida */ }
    }
    entry = { set: new Set(cells), resolutions: Array.from(resolutions) };
    cellSetCache.set(cells, entry);
  }
  return entry.resolutions.some((res) => entry!.set.has(latLngToCell(lat, lng, res)));
}
